import { useEffect, useState } from 'react';
import { useGetCurrentUserSettings } from '../services/react-query/users/useGetCurrentUserSettings';
import { useUpdateUserColumns } from '../services/react-query/users/useUpdateUserColumns';
import { ITableConfigurableModules } from '../types/Itable';

/**
 * Hook to manage user-selected table columns for a given module (e.g., 'accounts', 'tasks').
 *
 * Provides:
 * - The currently selected user columns for that module.
 * - A method to update the selected columns and save it to user settings.
 *
 * @param module - The module name (e.g., 'accounts', 'tasks', 'contacts').
 * @returns Object containing userColumns array and onUpdateColumns method.
 */
export const useUserColumns = (module: ITableConfigurableModules) => {
  const { data: userSettingsData } = useGetCurrentUserSettings();
  const { mutate: updateUserColumns } = useUpdateUserColumns();

  const [userColumns, setUserColumns] = useState<{ key: string }[]>([]);

  // Initialize user columns from user settings
  useEffect(() => {
    const columns = userSettingsData?.settings?.list_columns?.[module] || [];
    setUserColumns(columns);
  }, [userSettingsData, module]);

  /**
   * Updates the selected columns for the given module.
   *
   * @param columns - Array of column objects to be saved (e.g., [{ key: 'name' }, { key: 'status' }]).
   */
  const onUpdateColumns = (columns: { key: string }[]) => {
    const payload = {
      list_columns: {
        ...(userSettingsData?.settings?.list_columns || {}),
        [module]: columns,
      },
    };

    updateUserColumns(payload);
    setUserColumns(columns);
  };

  return { userColumns, onUpdateColumns };
};
